import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Mousewheel, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useTranslations } from '../../../localization/useTranslations';
import PrintAccessHandler from '../../../components/print-access-handler/PrintAccessHandler';
import SwiperCore from 'swiper';

interface MagazineSwiperProps {
  onSelectMagazine: (id: number | null) => void;
}

const MagazineSwiper: React.FC<MagazineSwiperProps> = ({ onSelectMagazine }) => {
  const allMagazines: Magazine[] = useMemo(() => window.Magazines || [], []);
  const [selectedYear, setSelectedYear] = useState<string>('All');
  const [filteredMagazines, setFilteredMagazines] = useState<Magazine[]>(allMagazines);
  const [activeMagazineId, setActiveMagazineId] = useState<number | null>(null);
  const translations = useTranslations();
  const swiperRef = useRef<SwiperCore | null>(null);

  // Create a unique set of years from magazine issue dates to filter magazines by year.
  const uniqueYears = Array.from(
    new Set(allMagazines.map((magazine) => new Date(magazine.issue_date || '').getFullYear()))
  ).filter(Boolean);

  useEffect(() => {
    const getFilteredMagazines = () => {
      if (selectedYear === 'All') {
        return allMagazines;
      } else {
        return allMagazines.filter((magazine) => {
          // Check if issue_date is not null and create Date object safely
          const year = magazine.issue_date ? new Date(magazine.issue_date).getFullYear().toString() : null;
          return year === selectedYear;
        });
      }
    };

    setFilteredMagazines(getFilteredMagazines());
  }, [selectedYear, allMagazines]);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
    }
  }, [filteredMagazines]);

  const handleSelectMagazine = (id: number) => {
    if (activeMagazineId === id) {
      setActiveMagazineId(null);
      setSelectedYear('All');
      onSelectMagazine(null);
    } else {
      setActiveMagazineId(id);
      onSelectMagazine(id);
    }
  };

  return (
    <div className="react-block__carousel react-block__carousel--magazine">
      <div className="year-selector">
        <label htmlFor="year-filter">{translations?.salesFunnel?.year || 'Year'}</label>
        <div className="year-filter__wrapper">
          <select id="year-filter" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            <option value="All">{translations?.printCategory?.allFilter || 'All'}</option>
            {uniqueYears.map((year) => (
              <option key={year} value={year.toString()}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Swiper
        onSwiper={(swiper: SwiperCore) => {
          swiperRef.current = swiper;
        }}
        modules={[Navigation, Pagination, Mousewheel, Keyboard]}
        className="react-block__swiper"
        spaceBetween={8}
        slidesPerView="auto"
        slideToClickedSlide={true}
        navigation
        pagination={{ clickable: true }}
      >
        {filteredMagazines.map((magazine) => (
          <SwiperSlide
            key={magazine.id}
            className={`magazine-filter ${activeMagazineId === magazine.id ? 'active' : ''}`}
            onClick={() => handleSelectMagazine(magazine.id)}
          >
            <img className="magazine-filter__image" src={magazine.featured_image || ''} alt="" />
            <div className="magazine-filter__text">
              <p className="magazine-filter__issue">{magazine.label ?? 'N/A'}</p>
              <p className="magazine-filter__name" dangerouslySetInnerHTML={{ __html: magazine.title }}></p>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <PrintAccessHandler />
    </div>
  );
};

export default MagazineSwiper;
